import { match } from '@frontend/duck-tape';
import type { ReactNode } from '@frontend/react';
import type { ClassName } from '@frontend/web-utils';
import { concatClasses, safeCss } from '@frontend/web-utils';
import { YStack } from './Stacks/YStack';

export type ScrollableContentPanelProps = {
  children: ReactNode;
  containerClassName?: ClassName;
  contentWidth?: ClassName;
  footer?: ReactNode;
  header?: ReactNode;
  justifyContent?: 'center' | 'end' | 'start';
};

export const ScrollableContentPanel = ({
  children,
  containerClassName,
  contentWidth: customContentWidth,
  footer,
  header,
  justifyContent = 'center',
}: ScrollableContentPanelProps) => {
  const contentWidth = customContentWidth || safeCss('w-full lg:w-3/5 xl:w-1/2');
  return (
    // On mobile browsers, the bottom url bar section is part of the viewport height, so padding-bottom is added to account for that space
    <YStack
      // eslint-disable-next-line tailwindcss/no-arbitrary-value
      className={concatClasses(
        'lg:py-xxxl 2xl:py-[100px] py-xl md:pb-md flex-1 justify-center pb-[100px] md:py-[60px] px-md md:px-none',
        containerClassName,
      )}
      gapY="md"
    >
      {header ? <YStack className={concatClasses('self-center', contentWidth)}>{header}</YStack> : null}
      <YStack
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className={concatClasses(
          'flex-1 overflow-scroll items-center no-scroll-bar',
          match(justifyContent)
            .with('end', () => safeCss('justify-end'))
            .with('start', () => safeCss('justify-start'))
            .with('center', () => safeCss('justify-center'))
            .exhaustive(),
        )}
        gapY="md"
      >
        <YStack className={contentWidth}>{children}</YStack>
      </YStack>
      <YStack className={concatClasses('self-center justify-center', contentWidth)}>{footer}</YStack>
    </YStack>
  );
};
